import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, Component, Input, HostBinding, ChangeDetectorRef, ViewChild, NgModule } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@simpl/element-ng/popover';
import { SiPopoverModule } from '@simpl/element-ng/popover';
import * as i3 from '@simpl/element-ng/resize-observer';
import { SiResizeObserverModule } from '@simpl/element-ng/resize-observer';
import * as i4 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';

const SI_FORM_VALIDATION_ERROR_MAPPER = new InjectionToken('SI_FORM_VALIDATION_ERROR_MAPPER', {
    factory: () => inject(SiFormValidationErrorMapperDefault),
    providedIn: 'root'
});
class SiFormValidationErrorMapperDefault {
    min = $localize `:@@SI_FORM_CONTAINER.ERROR.MIN:The value is too small`;
    max = $localize `:@@SI_FORM_CONTAINER.ERROR.MAX:The value is too large.`;
    required = $localize `:@@SI_FORM_CONTAINER.ERROR.REQUIRED:A value is required.`;
    requiredTrue = $localize `:@@SI_FORM_CONTAINER.ERROR.REQUIRED_TRUE:The value should be true.`;
    email = $localize `:@@SI_FORM_CONTAINER.ERROR.EMAIL:The email is not valid.`;
    minlength = $localize `:@@SI_FORM_CONTAINER.ERROR.MIN_LENGTH:The minimum number of characters is not met.`;
    maxlength = $localize `:@@SI_FORM_CONTAINER.ERROR.MAX_LENGTH:A maximum number of characters is exceeded.`;
    pattern = $localize `:@@SI_FORM_CONTAINER.ERROR.PATTERN:The value does not match the predefined pattern.`;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormValidationErrorMapperDefault, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormValidationErrorMapperDefault, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormValidationErrorMapperDefault, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SiFormContainerComponent {
    /**
     * Set the form entity to the container to enable the overall form validation on in
     * the form container edit panel.
     */
    form;
    /**
     * A form container in readonly mode is only displaying the form content without ability
     * to change it. The edit panel with typically save and cancel buttons is hidden. Set
     * to true to display the edit panel.
     */
    readonly = false;
    /**
     * The container hosts the form within a siResizeContainer to configure the breakpoint for
     * different screen sizes. Optionally, change the container breakpoints with the contentContainerBreakpoints
     * input.
     */
    contentContainerBreakpoints;
    /**
     * The title of the help link that is used to display the form validation problems within a popover.
     */
    helpTitle = $localize `:@@SI_FORM_CONTAINER.HELP:Help`;
    enableValidationHelp = false;
    /**
     * In some scenarios, one may not want the form container to be responsible for the layout relevant
     * `si-container-[xs|...]` classes, but let this be done by a different, nested component, e.g. by a
     * group box. In these cases, the property should be set to true.
     */
    disableContainerBreakpoints = false;
    /**
     * Every validation error has an errorCode. This map holds translate keys for error codes. The keys can
     * be used to display a translated message for each validation error. The defaults old english readable
     * key defaults for the Angular standard validators of the `Validators` class like `min`, `max` or `required`.
     *
     * Use the input to set your own translate keys for the form validators you need.
     */
    set errorCodeTranslateKeyMap(value) {
        this.buildFormErrorMapper(value);
    }
    /**
     * A map the maps from control names of the form to their translate keys.
     * The initial map is empty and the user is responsible to add the required
     * translate keys.
     */
    controlNameTranslateKeyMap = new Map();
    /**
     * A custom width value to be applied to all labels.
     *
     * @example labelWidth="100px".
     */
    labelWidth;
    trackByIndex = (index) => index;
    applicationFormErrorMapper = inject(SI_FORM_VALIDATION_ERROR_MAPPER, {
        optional: true
    });
    formErrorMapper;
    /**
     * Indicates whether the user interacted with the form.
     * @returns `true`, if the user selected at least one form element and
     * [blurred]{@link https://developer.mozilla.org/en-US/docs/Web/API/Element/blur_event} by losing focus again
     * (e.g. by setting focus on another element), or by editing the content of a form element.
     * Otherwise `false`.
     */
    get userInteractedWithForm() {
        return !!this.form && (this.form.dirty || this.form.touched);
    }
    /**
     * Indicates whether content editor message shall be styled as success.
     * @returns `true`, if {@link SiFormContainerComponent.userInteractedWithForm} is true and
     * the form is valid.
     */
    get validFormContainerMessage() {
        return this.userInteractedWithForm && this.form.status === 'VALID';
    }
    /**
     * Indicates whether the content editor message shall be styled as warning.
     * @returns `true`, if {@link SiFormContainerComponent.userInteractedWithForm} is true and
     * the form is invalid.
     */
    get invalidFormContainerMessage() {
        return this.userInteractedWithForm && this.form.status === 'INVALID';
    }
    /**
     * Returns the validation errors of the form's control when the control name is provided. Otherwise,
     * returns all validation errors of the form. Returns an empty arry when no form is available or if
     * the name does not match to a control.
     * @param controlName An optional name of a control that is part of the form.
     */
    getValidationErrors(controlName) {
        if (!this.form) {
            return [];
        }
        else if (!controlName) {
            return this.getFormValidationErrorsPrivate(this.form);
        }
        else {
            const control = this.form.get(controlName);
            if (control) {
                return this.getFormValidationErrorsPrivate(control, controlName);
            }
            else {
                return [];
            }
        }
    }
    getControlNameTranslateKey(controlName) {
        if (!this.controlNameTranslateKeyMap) {
            return undefined;
        }
        return this.controlNameTranslateKeyMap.get(controlName) ?? controlName;
    }
    getErrorCodeTranslateKey(errorCode, errorBody, controlName) {
        let errorResolver;
        if (controlName) {
            errorResolver = this.formErrorMapper[`${controlName}.${errorCode}`];
        }
        errorResolver ??= this.formErrorMapper[errorCode];
        if (typeof errorResolver === 'function') {
            return errorResolver(errorBody);
        }
        else {
            return errorResolver;
        }
    }
    buildFormErrorMapper(map) {
        let customMapper;
        if (map instanceof Map) {
            customMapper = Object.fromEntries(map.entries());
        }
        else if (map) {
            customMapper = map;
        }
        this.formErrorMapper = {
            ...this.applicationFormErrorMapper,
            ...customMapper
        };
    }
    getFormValidationErrorsPrivate(control, controlName) {
        let errors = [];
        // a form must either consist of
        // a) a formgroup (with nested form controls or groups) or
        // b) a single form control
        if (control instanceof FormGroup) {
            const formGroupErrors = this.getFormGroupErrors(control);
            errors = errors.concat(formGroupErrors);
            if (control.controls) {
                Object.keys(control.controls).forEach(key => {
                    const formGroupControl = control.controls[key];
                    const formGroupControlErrors = this.getFormValidationErrorsPrivate(formGroupControl, key);
                    errors = errors.concat(formGroupControlErrors);
                });
            }
        }
        else if (control instanceof FormControl) {
            const errorCodes = control.errors;
            if (errorCodes) {
                Object.entries(errorCodes).forEach(([errorCode, errorBody]) => {
                    errors.push({
                        controlName,
                        controlNameTranslationKey: controlName
                            ? this.getControlNameTranslateKey(controlName)
                            : undefined,
                        errorCode,
                        errorCodeTranslationKey: this.getErrorCodeTranslateKey(errorCode, errorBody, controlName),
                        errorParams: errorCodes[errorCode]
                    });
                });
            }
        }
        return errors;
    }
    getFormGroupErrors(formGroup) {
        const errors = [];
        const errorCodes = formGroup.errors;
        if (errorCodes) {
            Object.entries(errorCodes).forEach(([errorCode, errorBody]) => {
                errors.push({
                    errorCode,
                    errorCodeTranslationKey: this.getErrorCodeTranslateKey(errorCode, errorBody),
                    errorParams: errorCodes[errorCode]
                });
            });
        }
        return errors;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiFormContainerComponent, selector: "si-form-container", inputs: { form: "form", readonly: "readonly", contentContainerBreakpoints: "contentContainerBreakpoints", helpTitle: "helpTitle", enableValidationHelp: "enableValidationHelp", disableContainerBreakpoints: "disableContainerBreakpoints", errorCodeTranslateKeyMap: "errorCodeTranslateKeyMap", controlNameTranslateKeyMap: "controlNameTranslateKeyMap", labelWidth: "labelWidth" }, host: { properties: { "style.--si-form-label-width": "this.labelWidth" } }, ngImport: i0, template: "<div class=\"d-flex flex-column align-content-stretch flex-grow-1 flex-shrink-1\">\n  <ng-container [ngSwitch]=\"disableContainerBreakpoints\">\n    <div *ngSwitchCase=\"true\" class=\"flex-grow-1 flex-shrink-1 overflow-auto\">\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n    <div\n      *ngSwitchCase=\"false\"\n      class=\"flex-grow-1 flex-shrink-1 overflow-auto\"\n      siResponsiveContainer\n      [breakpoints]=\"contentContainerBreakpoints\"\n    >\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n  </ng-container>\n\n  <div *ngIf=\"!readonly\" class=\"d-flex flex-row flex-nowrap flex-grow-0 flex-shrink-0 p-4\">\n    <div\n      class=\"d-flex flex-grow-1 flex-shrink-1 justify-content-end text-end overflow-hidden mx-6 my-auto\"\n      [class.text-success]=\"validFormContainerMessage\"\n      [class.text-danger]=\"invalidFormContainerMessage\"\n    >\n      <ng-content select=\"[si-form-container-message]\"></ng-content>\n    </div>\n\n    <ng-container\n      *ngIf=\"enableValidationHelp && getValidationErrors() && invalidFormContainerMessage\"\n    >\n      <ng-template #popoverContent>\n        <ul class=\"p-0 list-unstyled\">\n          <li\n            *ngFor=\"let error of getValidationErrors(); trackBy: trackByIndex\"\n            class=\"form-container-help-popover-error\"\n          >\n            <b *ngIf=\"error.controlNameTranslationKey\"\n              >{{ error.controlNameTranslationKey | translate }}:\n            </b>\n            <span *ngIf=\"error.errorCode\">\n              {{ error.errorCodeTranslationKey || error.errorCode | translate : error.errorParams }}\n            </span>\n          </li>\n        </ul>\n      </ng-template>\n      <span\n        #popoverTrigger\n        class=\"form-container-edit-help ms-0 me-6 my-auto\"\n        triggers=\"click\"\n        containerClass=\"form-container-help-popover\"\n        tabindex=\"0\"\n        [innerHTML]=\"helpTitle | translate\"\n        [siPopover]=\"popoverContent\"\n        (keydown.enter)=\"popoverTrigger.click()\"\n      ></span>\n    </ng-container>\n\n    <div class=\"m-auto flex-grow-0 flex-shrink-0\">\n      <ng-content select=\"[si-form-container-buttons]\"></ng-content>\n    </div>\n  </div>\n</div>\n\n<ng-template #contentTemplate>\n  <ng-content select=\"[si-form-container-content]\"></ng-content>\n</ng-template>\n", styles: [":host{display:flex;flex:1 1 auto;flex-direction:column}:host ::slotted([si-form-container-message]),:host ::ng-deep [si-form-container-message]{max-inline-size:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.form-container-edit-help{text-decoration:underline;cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i2.SiPopoverDirective, selector: "[siPopover]", inputs: ["siPopover", "placement", "triggers", "popoverTitle", "containerClass", "outsideClick", "icon", "isOpen"], outputs: ["shown", "hidden"], exportAs: ["si-popover"] }, { kind: "directive", type: i3.SiResponsiveContainerDirective, selector: "[siResponsiveContainer]", inputs: ["resizeThrottle", "breakpoints"], exportAs: ["siResponsiveContainer"] }, { kind: "pipe", type: i4.SiTranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-container', template: "<div class=\"d-flex flex-column align-content-stretch flex-grow-1 flex-shrink-1\">\n  <ng-container [ngSwitch]=\"disableContainerBreakpoints\">\n    <div *ngSwitchCase=\"true\" class=\"flex-grow-1 flex-shrink-1 overflow-auto\">\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n    <div\n      *ngSwitchCase=\"false\"\n      class=\"flex-grow-1 flex-shrink-1 overflow-auto\"\n      siResponsiveContainer\n      [breakpoints]=\"contentContainerBreakpoints\"\n    >\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n  </ng-container>\n\n  <div *ngIf=\"!readonly\" class=\"d-flex flex-row flex-nowrap flex-grow-0 flex-shrink-0 p-4\">\n    <div\n      class=\"d-flex flex-grow-1 flex-shrink-1 justify-content-end text-end overflow-hidden mx-6 my-auto\"\n      [class.text-success]=\"validFormContainerMessage\"\n      [class.text-danger]=\"invalidFormContainerMessage\"\n    >\n      <ng-content select=\"[si-form-container-message]\"></ng-content>\n    </div>\n\n    <ng-container\n      *ngIf=\"enableValidationHelp && getValidationErrors() && invalidFormContainerMessage\"\n    >\n      <ng-template #popoverContent>\n        <ul class=\"p-0 list-unstyled\">\n          <li\n            *ngFor=\"let error of getValidationErrors(); trackBy: trackByIndex\"\n            class=\"form-container-help-popover-error\"\n          >\n            <b *ngIf=\"error.controlNameTranslationKey\"\n              >{{ error.controlNameTranslationKey | translate }}:\n            </b>\n            <span *ngIf=\"error.errorCode\">\n              {{ error.errorCodeTranslationKey || error.errorCode | translate : error.errorParams }}\n            </span>\n          </li>\n        </ul>\n      </ng-template>\n      <span\n        #popoverTrigger\n        class=\"form-container-edit-help ms-0 me-6 my-auto\"\n        triggers=\"click\"\n        containerClass=\"form-container-help-popover\"\n        tabindex=\"0\"\n        [innerHTML]=\"helpTitle | translate\"\n        [siPopover]=\"popoverContent\"\n        (keydown.enter)=\"popoverTrigger.click()\"\n      ></span>\n    </ng-container>\n\n    <div class=\"m-auto flex-grow-0 flex-shrink-0\">\n      <ng-content select=\"[si-form-container-buttons]\"></ng-content>\n    </div>\n  </div>\n</div>\n\n<ng-template #contentTemplate>\n  <ng-content select=\"[si-form-container-content]\"></ng-content>\n</ng-template>\n", styles: [":host{display:flex;flex:1 1 auto;flex-direction:column}:host ::slotted([si-form-container-message]),:host ::ng-deep [si-form-container-message]{max-inline-size:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.form-container-edit-help{text-decoration:underline;cursor:pointer}\n"] }]
        }], propDecorators: { form: [{
                type: Input
            }], readonly: [{
                type: Input
            }], contentContainerBreakpoints: [{
                type: Input
            }], helpTitle: [{
                type: Input
            }], enableValidationHelp: [{
                type: Input
            }], disableContainerBreakpoints: [{
                type: Input
            }], errorCodeTranslateKeyMap: [{
                type: Input
            }], controlNameTranslateKeyMap: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--si-form-label-width']
            }] } });

class SiFormItemComponent {
    /**
     * The ID of the input element, required to 'map' the label to it.
     * This allows for selecting the input element via click on the label, e.g.
     */
    inputId;
    /**
     * The label to be displayed in the form item.
     * It will be translated if a translation key is available.
     */
    label;
    /**
     * @deprecated This property has no effect and will be dropped.
     * Classes must be applied directly to the `si-form-item`.
     * In general, the label should not be styled.
     */
    labelClass;
    /** @deprecated this flag has no effect. Using colons is not intended by the design system. */
    labelColon = false;
    /**
     * A custom width value to be applied to the label.
     * A value of 0 is allowed as well to visually hide away the label area.
     *
     * Numbers will be converted to pixels.
     * Using numbers is discouraged and might be dropped.
     *
     * @example labelWidth="100px"
     */
    labelWidth;
    /**
     * @deprecated this flag has no effect. A label is always wrapped if it is too long. Clipping is not intended by the design system.
     */
    labelWrap = true;
    /** @deprecated this flag has no effect. An additional border is never shown.  */
    hideBorder = true;
    /**
     * @deprecated This property has no effect and will be dropped.
     * Classes must be applied directly to the `si-form-item`.
     * In general, the content should not be styled.
     */
    contentClass;
    /**
     * Use readonly to specify that the content cannot be modified.
     */
    readonly = false;
    /**
     * Defines that this form item is required for the overall form to be valid.
     */
    required = false;
    /**
     * A reference to the content of the form item.
     */
    content;
    get labelWidthCssVar() {
        if (typeof this.labelWidth === 'number') {
            return `${this.labelWidth}px`;
        }
        return this.labelWidth;
    }
    get isCheckItem() {
        return (this.relevantInputElement instanceof HTMLInputElement &&
            (this.relevantInputElement.type === 'checkbox' || this.relevantInputElement.type === 'radio'));
    }
    contentChildren;
    cdRef = inject(ChangeDetectorRef);
    relevantInputElement;
    ngAfterViewInit() {
        this.contentChildren = this.content.nativeElement.getElementsByTagName('*');
        this.afterViewInitRespectingChangeDetection();
    }
    get displayLabel() {
        return !!this.label && this.label.trim().length > 0;
    }
    afterViewInitRespectingChangeDetection() {
        queueMicrotask(() => {
            this.relevantInputElement = this.getRelevantInputElement();
            this.mapLabelToInput();
            this.cdRef.markForCheck();
        });
    }
    getRelevantInputElement() {
        const contentChildren = this.contentChildren ?? this.content.nativeElement.getElementsByTagName('*');
        const relevantInputTypes = ['input', 'meter', 'progress', 'select', 'textarea'];
        let currentChildElement;
        if (contentChildren) {
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < contentChildren.length; i++) {
                currentChildElement = contentChildren[i];
                if (relevantInputTypes.includes(currentChildElement.nodeName.toLowerCase())) {
                    return currentChildElement;
                }
            }
        }
        return undefined;
    }
    mapLabelToInput() {
        // only necessary if we do not know the inputId
        if (!this.inputId && this.relevantInputElement?.id) {
            this.inputId = this.relevantInputElement.id;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiFormItemComponent, selector: "si-form-item", inputs: { inputId: "inputId", label: "label", labelClass: "labelClass", labelColon: "labelColon", labelWidth: "labelWidth", labelWrap: "labelWrap", hideBorder: "hideBorder", contentClass: "contentClass", readonly: "readonly", required: "required" }, host: { properties: { "class.readonly": "this.readonly", "class.required": "this.required", "style.--si-form-label-width": "this.labelWidthCssVar" } }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"form-item-label-wrapper\">\n  <label\n    *ngIf=\"displayLabel\"\n    class=\"form-label\"\n    [class.si-form-label-for-check]=\"isCheckItem\"\n    [class.required]=\"required && !readonly\"\n    [for]=\"inputId\"\n  >\n    <span class=\"form-item-label-text text-break\" [class.d-none]=\"labelWidth === 0\">{{\n      label! | translate\n    }}</span>\n  </label>\n</div>\n<div #content class=\"form-item-content\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{margin-block-end:12px;display:flex;flex-direction:column;padding-inline:2px}.form-item-content{flex:1 1 auto;display:flex;align-items:flex-end;flex-wrap:wrap;position:relative}:host-context(.si-container-sm),:host-context(.si-container-md),:host-context(.si-container-lg),:host-context(.si-container-xl),:host-context(.si-container-xxl){flex-direction:row}:host-context(.si-container-sm) .form-item-label-wrapper,:host-context(.si-container-md) .form-item-label-wrapper,:host-context(.si-container-lg) .form-item-label-wrapper,:host-context(.si-container-xl) .form-item-label-wrapper,:host-context(.si-container-xxl) .form-item-label-wrapper{inline-size:var(--si-form-label-width, 16.6666666667%)}:host-context(.si-container-sm) .form-label,:host-context(.si-container-md) .form-label,:host-context(.si-container-lg) .form-label,:host-context(.si-container-xl) .form-label,:host-context(.si-container-xxl) .form-label{padding-block:8px;padding-inline:16px;margin-block:0;line-height:1.333}:host-context(.si-container-sm) .form-label.si-form-label-for-check,:host-context(.si-container-md) .form-label.si-form-label-for-check,:host-context(.si-container-lg) .form-label.si-form-label-for-check,:host-context(.si-container-xl) .form-label.si-form-label-for-check,:host-context(.si-container-xxl) .form-label.si-form-label-for-check{padding-block:2px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i4.SiTranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-item', template: "<div class=\"form-item-label-wrapper\">\n  <label\n    *ngIf=\"displayLabel\"\n    class=\"form-label\"\n    [class.si-form-label-for-check]=\"isCheckItem\"\n    [class.required]=\"required && !readonly\"\n    [for]=\"inputId\"\n  >\n    <span class=\"form-item-label-text text-break\" [class.d-none]=\"labelWidth === 0\">{{\n      label! | translate\n    }}</span>\n  </label>\n</div>\n<div #content class=\"form-item-content\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{margin-block-end:12px;display:flex;flex-direction:column;padding-inline:2px}.form-item-content{flex:1 1 auto;display:flex;align-items:flex-end;flex-wrap:wrap;position:relative}:host-context(.si-container-sm),:host-context(.si-container-md),:host-context(.si-container-lg),:host-context(.si-container-xl),:host-context(.si-container-xxl){flex-direction:row}:host-context(.si-container-sm) .form-item-label-wrapper,:host-context(.si-container-md) .form-item-label-wrapper,:host-context(.si-container-lg) .form-item-label-wrapper,:host-context(.si-container-xl) .form-item-label-wrapper,:host-context(.si-container-xxl) .form-item-label-wrapper{inline-size:var(--si-form-label-width, 16.6666666667%)}:host-context(.si-container-sm) .form-label,:host-context(.si-container-md) .form-label,:host-context(.si-container-lg) .form-label,:host-context(.si-container-xl) .form-label,:host-context(.si-container-xxl) .form-label{padding-block:8px;padding-inline:16px;margin-block:0;line-height:1.333}:host-context(.si-container-sm) .form-label.si-form-label-for-check,:host-context(.si-container-md) .form-label.si-form-label-for-check,:host-context(.si-container-lg) .form-label.si-form-label-for-check,:host-context(.si-container-xl) .form-label.si-form-label-for-check,:host-context(.si-container-xxl) .form-label.si-form-label-for-check{padding-block:2px}\n"] }]
        }], propDecorators: { inputId: [{
                type: Input
            }], label: [{
                type: Input
            }], labelClass: [{
                type: Input
            }], labelColon: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], labelWrap: [{
                type: Input
            }], hideBorder: [{
                type: Input
            }], contentClass: [{
                type: Input
            }], readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }, {
                type: Input
            }], required: [{
                type: HostBinding,
                args: ['class.required']
            }, {
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], labelWidthCssVar: [{
                type: HostBinding,
                args: ['style.--si-form-label-width']
            }] } });

class SiFormModule {
    /**
     * Overrides the default configuration of the form module.
     *
     * @param errorMapper The error mappers is used to resolve angular validation errors.
     * It will be merged with already existing error mappers.
     */
    // We have the errorMapper wrapped in an object, to allow the addition of more configuration fields without breaking the API.
    static withConfiguration({ validationErrorMapper }) {
        return {
            ngModule: SiFormModule,
            providers: [
                {
                    provide: SI_FORM_VALIDATION_ERROR_MAPPER,
                    useFactory: () => ({
                        ...inject(SiFormValidationErrorMapperDefault),
                        ...inject(SI_FORM_VALIDATION_ERROR_MAPPER, { skipSelf: true, optional: true }),
                        ...validationErrorMapper
                    })
                }
            ]
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiFormModule, declarations: [SiFormContainerComponent, SiFormItemComponent], imports: [CommonModule, SiPopoverModule, SiResizeObserverModule, SiTranslateModule], exports: [SiFormContainerComponent, SiFormItemComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormModule, imports: [CommonModule, SiPopoverModule, SiResizeObserverModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiFormModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiFormContainerComponent, SiFormItemComponent],
                    imports: [CommonModule, SiPopoverModule, SiResizeObserverModule, SiTranslateModule],
                    exports: [SiFormContainerComponent, SiFormItemComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiFormContainerComponent, SiFormItemComponent, SiFormModule };
